import type { AuditEntry, User } from '../types';
import { auditLog } from './mockData';
import { respond } from './respond';
import { addDoc, collection, getDocs, limit, orderBy, query } from 'firebase/firestore';
import { auth, db, firebaseEnabled } from '../lib/firebase';

export type Actor = Pick<User, 'id' | 'name' | 'role'>;

// Targets must be IDs or clinic names only, never patient names or phones.
export function addAudit(actor: Actor | undefined, action: string, target: string, facilityId?: string, detail?: string, at = new Date().toISOString()): void {
  // Real mode: append-only entry in the database. The rules require actorId = the signed-in user.
  if (firebaseEnabled && db && auth?.currentUser) {
    const actorId = auth.currentUser.uid;
    const entry = { actorId, actor: actor?.name ?? 'Unknown', role: actor?.role ?? 'staff', action, target, at, ...(detail ? { detail } : {}), ...(facilityId ? { facilityId } : {}) };
    const clinicUser = actor?.role && actor.role !== 'super_admin';
    const path = facilityId && clinicUser ? collection(db, 'facilities', facilityId, 'audit') : collection(db, 'platformAudit');
    addDoc(path, entry).catch((err) => console.error('Audit write failed', err));
    return;
  }
  auditLog.push({
    id: `a-${Date.now()}-${auditLog.length}`,
    facilityId,
    userId: actor?.id ?? 'unknown',
    actor: actor?.name ?? 'Unknown',
    role: actor?.role ?? 'staff',
    action,
    target,
    detail,
    at,
  });
}

// Super admin: the platform log plus each clinic's log (IDs and clinic names only, never patient details).
export async function getAuditLog(): Promise<AuditEntry[]> {
  if (firebaseEnabled && db) {
    const d = db;
    const read = async (path: ReturnType<typeof collection>, fid?: string): Promise<AuditEntry[]> => {
      const snap = await getDocs(query(path, orderBy('at', 'desc'), limit(300)));
      return snap.docs.map((x) => {
        const e = x.data() as AuditEntry & { actorId: string };
        return { ...e, id: x.id, userId: e.actorId, facilityId: e.facilityId ?? fid };
      });
    };
    const fs = await getDocs(collection(d, 'facilities'));
    const lists = await Promise.all([
      read(collection(d, 'platformAudit')),
      ...fs.docs.map((f) => read(collection(d, 'facilities', f.id, 'audit'), f.id).catch(() => [])),
    ]);
    return lists.flat().sort((a, b) => b.at.localeCompare(a.at));
  }
  return respond([...auditLog].sort((a, b) => b.at.localeCompare(a.at)));
}
